import React, { forwardRef, useEffect, useState } from "react";
import "./FormSelect.scss";
import Chevron from "./Chevron/Chevron";
import { FieldErrors } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useComponentVisible } from "../../../../utils/functions";
import getClasses from "../../../../utils/getClasses";

export interface IOption {
  [key: string]: string;
}
interface OptionProps {
  options: IOption[];
  defaultValue?: string;
  isInvalid?: boolean;
  transparent?: boolean;
  label: string;
  disabled?: boolean;
  placeholder: string;
  required?: boolean;
  typeErrors?: string;
  wrapperClass?: string;
  onChange: (value: string) => void;
}

const FormSelect = forwardRef<HTMLElement, OptionProps>((props, refinput) => {
  const {
    options,
    label,
    placeholder,
    typeErrors,
    defaultValue,
    disabled,
    required,
    isInvalid,
    onChange,
    wrapperClass = "col-lg-4",
  } = props;

  const { t } = useTranslation();
  useEffect(() => {
    defaultValue &&
      options &&
      options.some(
        (option) =>
          Object.values(option)[0] === defaultValue && setSelectedOption(option)
      );
  }, [defaultValue, options]);

  const { ref, isComponentVisible, setIsComponentVisible } =
    useComponentVisible(false);
  const [selectedOption, setSelectedOption] = useState<IOption | undefined>(
    undefined
  );

  const toggleSelect = () => setIsComponentVisible(!isComponentVisible);

  const handleOptionClick = (option: IOption) => {
    const value = option[Object.keys(option)[0]];
    setSelectedOption(option);
    setIsComponentVisible(false);
    onChange(value);
  };

  return (
    <div className={wrapperClass}>
      <div className="form-group offset-lg-0 mt-1">
        <label
          htmlFor="name"
          className={`form-control-label ${getClasses({
            "title-red": isInvalid,
          })}`}
        >
          {label} {required && "*"}
        </label>
        <div ref={ref} className="custom-select-container">
          <div
            className={`custom-select ${props.transparent && "transparent"} ${
              isComponentVisible ? "open" : ""
            } ${isInvalid ? "form-control is-invalid" : ""} ${getClasses({
              disabled,
            })}`}
            onClick={toggleSelect}
          >
            <div className="text-select">
              {selectedOption && Object.keys(selectedOption)[1] && (
                <i
                  style={{
                    color: Object.keys(selectedOption)[2],
                  }}
                  className={`icon ${Object.keys(selectedOption)[1]}`}
                ></i>
              )}
              {selectedOption
                ? Object.keys(selectedOption)[2]
                  ? Object.keys(selectedOption)[0]
                  : t(`selectOptions.${Object.keys(selectedOption)[0]}`)
                : placeholder}
            </div>
            <Chevron
              direction={isComponentVisible && !disabled ? "top" : "bottom"}
            />
          </div>
          {isComponentVisible && !disabled && (
            <div className="custom-options-container">
              {options.map((option, index) => (
                <div
                  key={index}
                  className={`custom-option ${
                    selectedOption &&
                    Object.keys(selectedOption)[0] ===
                      option[Object.keys(option)[0]]
                      ? "selected"
                      : ""
                  }`}
                  onClick={() => handleOptionClick(option)}
                >
                  {Object.keys(option)[1] && (
                    <i
                      style={{
                        color: Object.keys(option)[2],
                      }}
                      className={`icon ${Object.keys(option)[1]}`}
                    ></i>
                  )}
                  {Object.keys(option)[2]
                    ? Object.keys(option)[0]
                    : t(`selectOptions.${Object.keys(option)[0]}`)}
                </div>
              ))}
            </div>
          )}
        </div>
        {typeErrors && (
          <span
            className={`form-control-label ${getClasses({
              "title-red": isInvalid,
            })}`}
          >
            {typeErrors}
          </span>
        )}
      </div>
    </div>
  );
});

export default FormSelect;
